import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { readResponse } from '../utils/fetchSafe';
import Layout from '../components/Layout';
import ConfirmDialog from '../components/ConfirmDialog';
import { customersAPI, projectsAPI, showConfirmModal } from '../services/api';
import { toast } from 'sonner';
import { Edit, X } from 'lucide-react';
import { formatPhoneNumber } from '../utils/formatters';
import { useAuth } from '../context/AuthContext';
import MapsLink from '../components/MapsLink';

const CustomerDetail = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  // Customer Information fields
  const [type, setType] = useState('Residential');
  const [customerNumber, setCustomerNumber] = useState('');
  const [company, setCompany] = useState('');
  const [primaryLastName, setPrimaryLastName] = useState('');
  const [primaryFirstName, setPrimaryFirstName] = useState('');
  const [primaryPhone, setPrimaryPhone] = useState('');
  const [alternateLastName, setAlternateLastName] = useState('');
  const [alternateFirstName, setAlternateFirstName] = useState('');
  const [alternatePhone, setAlternatePhone] = useState('');
  const [email, setEmail] = useState('');

  // Addresses and projects from database
  const [addresses, setAddresses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  
  // New address form fields
  const [newAddress, setNewAddress] = useState({
    streetAddress: '',
    unit: '',
    city: '',
    state: '',
    zip: '',
    billTo: false,
    current: true
  });
  
  // Delete confirmation dialog
  const [deleteAddressDialog, setDeleteAddressDialog] = useState({ isOpen: false, index: null });

  // Customer ID override (permission-gated)
  const canEditCustomerId = !!user?.is_admin || hasPermission('update_customer_ids');
  const [editingCustomerId, setEditingCustomerId] = useState(false);
  const [customerIdDraft, setCustomerIdDraft] = useState('');
  const [savingCustomerId, setSavingCustomerId] = useState(false);

  const handleSaveCustomerId = async () => {
    const next = customerIdDraft.trim();
    if (!next || next === customerNumber) {
      setEditingCustomerId(false);
      return;
    }
    if (!window.confirm(`Override customer ID from "${customerNumber}" to "${next}"?\n\nAll existing project IDs for this customer will be renumbered to match (e.g. "${customerNumber}-1" → "${next}-1"). This cannot be undone.`)) {
      return;
    }
    setSavingCustomerId(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${encodeURIComponent(customerNumber)}/customer-id`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ customer_id: next }),
      });
      // Read defensively — PostHog in production locks the body stream
      // before our code can read it, which surfaces as "body stream
      // already read" and made the post-save navigate never run even
      // though the rename had succeeded server-side.
      const { data } = await readResponse(res);
      if (!res.ok) throw new Error(data?.detail || `Failed to override customer ID (HTTP ${res.status})`);
      const renamed = data.projects_renumbered || 0;
      toast.success(`Customer ID changed to ${next}.${renamed ? ` ${renamed} project${renamed === 1 ? '' : 's'} renumbered.` : ''}`);
      setEditingCustomerId(false);
      // Navigate to the new URL since customerId is path-bound.
      navigate(`/customers/${encodeURIComponent(next)}`, { replace: true });
    } catch (e) {
      toast.error(e.message || 'Failed to override customer ID');
    } finally {
      setSavingCustomerId(false);
    }
  };

  useEffect(() => {
    fetchCustomerDetails();
  }, [customerId]);

  const fetchCustomerDetails = async () => {
    try {
      setLoading(true);
      const data = await customersAPI.getOne(customerId);
      setCustomer(data);
      
      // Populate form fields (handle both old and new field names)
      setType(data.type || 'Residential');
      setCustomerNumber(data.customer_id || data.number || '');
      setCompany(data.company || '');
      setPrimaryFirstName(data.primary_first_name || data.first_name || '');
      setPrimaryLastName(data.primary_last_name || data.last_name || '');
      setPrimaryPhone(data.primary_phone || '');
      setAlternateFirstName(data.alternate_first_name || '');
      setAlternateLastName(data.alternate_last_name || '');
      setAlternatePhone(data.alternate_phone || '');
      setEmail(data.email || '');
      
      // Load addresses from customer data
      if (data.addresses && data.addresses.length > 0) {
        setAddresses(data.addresses.map(addr => ({
          streetAddress: addr.street_address || '',
          unit: addr.unit || '',
          city: addr.city || '',
          state: addr.state || '',
          zip: addr.zip || '',
          billTo: addr.bill_to ? 'Yes' : 'No',
          current: addr.current ? 'Yes' : 'No'
        })));
      } else {
        setAddresses([]);
      }
      
      // Fetch projects for this customer
      try {
        const projectsData = await projectsAPI.getByCustomer(customerId);
        if (projectsData.projects && projectsData.projects.length > 0) {
          setProjects(projectsData.projects.map(p => ({
            id: p.project_id,
            streetAddress: p.address || p.job_location || '',
            location: p.territory || '',
            type: p.job_type || '',
            pndDate: p.pnd_date || p.fnol_date || '',
            startDate: p.start_date || 'n/a',
            completionDate: p.completion_date || p.finish_date || 'n/a'
          })));
        } else {
          setProjects([]);
        }
      } catch (projectError) {
        console.error('Error fetching projects:', projectError);
        setProjects([]);
      }
      
    } catch (error) {
      toast.error('Failed to fetch customer details');
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteProject = async (projectId) => {
    if (!(await showConfirmModal('Are you sure you want to delete this project?'))) return;
    try {
      await projectsAPI.delete(projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));
      toast.success('Project deleted');
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error('Failed to delete project');
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const updateData = {
        type: type,
        company: company,
        primary_last_name: primaryLastName,
        primary_first_name: primaryFirstName,
        primary_phone: primaryPhone,
        alternate_last_name: alternateLastName,
        alternate_first_name: alternateFirstName,
        alternate_phone: alternatePhone,
        email: email
      };
      
      await customersAPI.update(customerId, updateData);
      
      toast.success('Customer saved successfully');
      
    } catch (error) {
      console.error('Error saving customer:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to save customer');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    // Honor the browser's history so the user returns to whichever
    // board / search / detail page they came from. Falls back to the
    // All Customers list if this is a fresh tab with no prior entry
    // (history idx 0 means CustomerDetail is the first navigation).
    const idx = window.history.state && typeof window.history.state.idx === 'number' ? window.history.state.idx : 0;
    if (idx > 0) {
      navigate(-1);
    } else {
      navigate('/customers');
    }
  };

  // Address management functions
  const handleAddNewAddress = () => {
    setShowAddressForm(true);
    setEditingAddressIndex(null);
    setNewAddress({
      streetAddress: '',
      unit: '',
      city: '',
      state: '',
      zip: '',
      billTo: false,
      current: true
    });
  };

  const handleSaveAddress = async () => {
    if (!newAddress.streetAddress) {
      toast.error('Street address is required');
      return;
    }

    const addressData = {
      street_address: newAddress.streetAddress,
      unit: newAddress.unit,
      city: newAddress.city,
      state: newAddress.state,
      zip: newAddress.zip,
      bill_to: newAddress.billTo,
      current: newAddress.current
    };

    let updatedAddresses;
    if (editingAddressIndex !== null) {
      // Update existing address
      updatedAddresses = addresses.map((addr, idx) => 
        idx === editingAddressIndex ? {
          streetAddress: newAddress.streetAddress,
          unit: newAddress.unit,
          city: newAddress.city,
          state: newAddress.state,
          zip: newAddress.zip,
          billTo: newAddress.billTo ? 'Yes' : 'No',
          current: newAddress.current ? 'Yes' : 'No'
        } : addr
      );
    } else {
      // Add new address
      updatedAddresses = [...addresses, {
        streetAddress: newAddress.streetAddress,
        city: newAddress.city,
        state: newAddress.state,
        zip: newAddress.zip,
        billTo: newAddress.billTo ? 'Yes' : 'No',
        current: newAddress.current ? 'Yes' : 'No'
      }];
    }

    try {
      // Save to backend
      const backendAddresses = updatedAddresses.map(addr => ({
        street_address: addr.streetAddress,
        unit: addr.unit || '',
        city: addr.city,
        state: addr.state,
        zip: addr.zip,
        bill_to: addr.billTo === 'Yes',
        current: addr.current === 'Yes'
      }));

      const resp = await customersAPI.update(customerId, { addresses: backendAddresses });

      setAddresses(updatedAddresses);
      setShowAddressForm(false);
      setEditingAddressIndex(null);
      const cascaded = resp?.projects_address_cascade?.updated || 0;
      const baseMsg = editingAddressIndex !== null ? 'Address updated successfully' : 'Address added successfully';
      const cascadeMsg = cascaded > 0 ? ` ${cascaded} project${cascaded === 1 ? '' : 's'} updated to match.` : '';
      toast.success(baseMsg + cascadeMsg);
    } catch (error) {
      console.error('Error saving address:', error);
      if (error.response?.status !== 401) {
        toast.error(error.response?.data?.detail || 'Failed to save address');
      }
    }
  };

  const handleEditAddress = (index) => {
    const addr = addresses[index];
    setNewAddress({
      streetAddress: addr.streetAddress,
      unit: addr.unit || '',
      city: addr.city,
      state: addr.state,
      zip: addr.zip,
      billTo: addr.billTo === 'Yes',
      current: addr.current === 'Yes'
    });
    setEditingAddressIndex(index);
    setShowAddressForm(true);
  };

  const handleDeleteAddress = (index) => {
    setDeleteAddressDialog({ isOpen: true, index });
  };

  const confirmDeleteAddress = async () => {
    const index = deleteAddressDialog.index;
    setDeleteAddressDialog({ isOpen: false, index: null });
    
    const updatedAddresses = addresses.filter((_, idx) => idx !== index);
    
    try {
      const backendAddresses = updatedAddresses.map(addr => ({
        street_address: addr.streetAddress,
        unit: addr.unit || '',
        city: addr.city,
        state: addr.state,
        zip: addr.zip,
        bill_to: addr.billTo === 'Yes',
        current: addr.current === 'Yes'
      }));

      await customersAPI.update(customerId, { addresses: backendAddresses });
      
      setAddresses(updatedAddresses);
      toast.success('Address deleted successfully');
    } catch (error) {
      console.error('Error deleting address:', error);
      toast.error('Failed to delete address');
    }
  };

  const cancelDeleteAddress = () => {
    setDeleteAddressDialog({ isOpen: false, index: null });
  };

  const handleCancelAddressForm = () => {
    setShowAddressForm(false);
    setEditingAddressIndex(null);
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8 text-center">
          <div className="text-gray-500">Loading customer details...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="bg-gray-50 min-h-screen p-8">
        {/* Header */}
        <div className="mb-6">
          <div className="text-gray-500 text-lg flex items-center gap-2" data-testid="customer-header">
            <span>Customer #</span>
            {editingCustomerId ? (
              <>
                <input
                  type="text"
                  value={customerIdDraft}
                  onChange={(e) => setCustomerIdDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveCustomerId();
                    if (e.key === 'Escape') { setEditingCustomerId(false); setCustomerIdDraft(customerNumber); }
                  }}
                  autoFocus
                  className="border border-gray-300 rounded px-2 py-1 text-base w-44"
                  data-testid="customer-id-input"
                />
                <button
                  onClick={handleSaveCustomerId}
                  disabled={savingCustomerId}
                  className="bg-[#5BC0DE] hover:bg-[#46b8da] disabled:opacity-50 text-white text-sm px-3 py-1 rounded"
                  data-testid="customer-id-save"
                >
                  {savingCustomerId ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => { setEditingCustomerId(false); setCustomerIdDraft(customerNumber); }}
                  className="border border-gray-300 hover:bg-gray-100 text-gray-600 text-sm px-3 py-1 rounded"
                  data-testid="customer-id-cancel"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <span data-testid="customer-id-display">{customerNumber}</span>
                {canEditCustomerId && (
                  <button
                    onClick={() => { setCustomerIdDraft(customerNumber); setEditingCustomerId(true); }}
                    className="text-gray-400 hover:text-[#5B8DB8] p-1"
                    title="Override customer ID. Will cascade to all existing project IDs for this customer."
                    data-testid="customer-id-edit-btn"
                  >
                    <Edit size={14} />
                  </button>
                )}
              </>
            )}
          </div>
          <h1 className="text-3xl text-gray-600">
            {primaryLastName && primaryLastName !== '-' ? primaryLastName : ''}, {primaryFirstName}
          </h1>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-2 gap-6">
          {/* Left Column - Customer Information */}
          <div className="bg-white rounded shadow p-6">
            <h2 className="text-xl text-[#5B8DB8] font-light mb-6">Customer Information</h2>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="text-red-500 text-sm w-40 text-right">Type:</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 flex-1"
                >
                  <option value="Residential">Residential</option>
                  <option value="Commercial">Commercial</option>
                </select>
              </div>

              <div className="flex items-center gap-4">
                <label className="text-sm w-40 text-right">Customer Number:</label>
                {canEditCustomerId ? (
                  <>
                    <input
                      type="text"
                      value={editingCustomerId ? customerIdDraft : customerNumber}
                      onFocus={() => {
                        if (!editingCustomerId) {
                          setCustomerIdDraft(customerNumber);
                          setEditingCustomerId(true);
                        }
                      }}
                      onChange={(e) => setCustomerIdDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveCustomerId();
                        if (e.key === 'Escape') { setEditingCustomerId(false); setCustomerIdDraft(customerNumber); }
                      }}
                      className="border border-gray-300 rounded px-3 py-2 flex-1"
                      data-testid="customer-id-form-input"
                      title="Override customer ID. Will cascade to all existing project IDs for this customer."
                    />
                    {editingCustomerId && (
                      <>
                        <button
                          onClick={handleSaveCustomerId}
                          disabled={savingCustomerId}
                          className="bg-[#5BC0DE] hover:bg-[#46b8da] disabled:opacity-50 text-white text-sm px-3 py-2 rounded"
                          data-testid="customer-id-form-save"
                        >
                          {savingCustomerId ? 'Saving…' : 'Save ID'}
                        </button>
                        <button
                          onClick={() => { setEditingCustomerId(false); setCustomerIdDraft(customerNumber); }}
                          className="border border-gray-300 hover:bg-gray-100 text-gray-600 text-sm px-3 py-2 rounded"
                          data-testid="customer-id-form-cancel"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <input
                    type="text"
                    value={customerNumber}
                    className="border border-gray-300 rounded px-3 py-2 flex-1"
                    disabled
                  />
                )}
              </div>

              <div className="flex items-center gap-4">
                <label className="text-sm w-40 text-right">Company:</label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 flex-1"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="text-red-500 text-sm w-40 text-right">Primary Last Name:</label>
                <input
                  type="text"
                  value={primaryLastName}
                  onChange={(e) => setPrimaryLastName(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 flex-1"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="text-red-500 text-sm w-40 text-right">Primary First Name:</label>
                <input
                  type="text"
                  value={primaryFirstName}
                  onChange={(e) => setPrimaryFirstName(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 flex-1"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="text-red-500 text-sm w-40 text-right">Primary Phone:</label>
                <input
                  type="text"
                  value={primaryPhone}
                  onChange={(e) => setPrimaryPhone(formatPhoneNumber(e.target.value))}
                  placeholder="215-555-1234"
                  className="border border-gray-300 rounded px-3 py-2 flex-1"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="text-sm w-40 text-right">Alternate Last Name:</label>
                <input
                  type="text"
                  value={alternateLastName}
                  onChange={(e) => setAlternateLastName(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 flex-1"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="text-sm w-40 text-right">Alternate First Name:</label>
                <input
                  type="text"
                  value={alternateFirstName}
                  onChange={(e) => setAlternateFirstName(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 flex-1"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="text-sm w-40 text-right">Alternate Phone:</label>
                <input
                  type="text"
                  value={alternatePhone}
                  onChange={(e) => setAlternatePhone(formatPhoneNumber(e.target.value))}
                  placeholder="215-555-1234"
                  className="border border-gray-300 rounded px-3 py-2 flex-1"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="text-red-500 text-sm w-40 text-right">Email:</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2 flex-1"
                />
              </div>
            </div>
          </div>

          {/* Right Column - Addresses and Projects */}
          <div className="space-y-6">
            {/* Customer Addresses */}
            <div className="bg-white rounded shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl text-[#5B8DB8] font-light">Customer Addresses</h2>
                <button 
                  onClick={handleAddNewAddress}
                  className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-4 py-2 rounded transition-colors text-sm"
                >
                  + New Address
                </button>
              </div>

              {/* Address Form */}
              {showAddressForm && (
                <div className="mb-4 p-4 border border-gray-200 rounded bg-gray-50">
                  <h3 className="text-sm font-medium mb-3">
                    {editingAddressIndex !== null ? 'Edit Address' : 'Add New Address'}
                  </h3>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-gray-600">Street Address *</label>
                      <input
                        type="text"
                        value={newAddress.streetAddress}
                        onChange={(e) => setNewAddress({...newAddress, streetAddress: e.target.value})}
                        className="border border-gray-300 rounded px-2 py-1 w-full text-sm"
                        data-testid="address-street-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Unit/Apt/Suite</label>
                      <input
                        type="text"
                        value={newAddress.unit}
                        onChange={(e) => setNewAddress({...newAddress, unit: e.target.value})}
                        className="border border-gray-300 rounded px-2 py-1 w-full text-sm"
                        data-testid="address-unit-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">City</label>
                      <input
                        type="text"
                        value={newAddress.city}
                        onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                        className="border border-gray-300 rounded px-2 py-1 w-full text-sm"
                        data-testid="address-city-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">State</label>
                      <input
                        type="text"
                        value={newAddress.state}
                        onChange={(e) => setNewAddress({...newAddress, state: e.target.value})}
                        className="border border-gray-300 rounded px-2 py-1 w-full text-sm"
                        data-testid="address-state-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Zip</label>
                      <input
                        type="text"
                        value={newAddress.zip}
                        onChange={(e) => setNewAddress({...newAddress, zip: e.target.value})}
                        className="border border-gray-300 rounded px-2 py-1 w-full text-sm"
                        data-testid="address-zip-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-4 mb-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={newAddress.current}
                        onChange={(e) => setNewAddress({...newAddress, current: e.target.checked})}
                        data-testid="address-current-checkbox"
                      />
                      Current Address
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={newAddress.billTo}
                        onChange={(e) => setNewAddress({...newAddress, billTo: e.target.checked})}
                        data-testid="address-billing-checkbox"
                      />
                      Billing Address
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveAddress}
                      className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-4 py-1 rounded text-sm"
                      data-testid="save-address-btn"
                    >
                      Save Address
                    </button>
                    <button
                      onClick={handleCancelAddressForm}
                      className="border border-gray-300 hover:bg-gray-100 text-gray-600 px-4 py-1 rounded text-sm"
                      data-testid="cancel-address-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2">
                      <th className="text-left p-2 text-gray-600 font-normal">Street Address</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Unit/Apt/Suite</th>
                      <th className="text-left p-2 text-gray-600 font-normal">City</th>
                      <th className="text-left p-2 text-gray-600 font-normal">State</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Zip</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Current</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Billing</th>
                      <th className="text-left p-2 text-gray-600 font-normal"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {addresses.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="p-4 text-center text-gray-500 italic">
                          No addresses found
                        </td>
                      </tr>
                    ) : (
                      addresses.map((address, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50" data-testid={`address-row-${index}`}>
                          <td className="p-2">
                            {(() => {
                              if (!address.streetAddress) return '';
                              const full = [address.streetAddress, address.unit, address.city, address.state, address.zip].filter(Boolean).join(', ');
                              return (
                                <MapsLink address={full} testid={`address-map-link-${index}`}>
                                  {address.streetAddress}
                                </MapsLink>
                              );
                            })()}
                          </td>
                          <td className="p-2 text-gray-700">{address.unit || ''}</td>
                          <td className="p-2 text-gray-700">{address.city}</td>
                          <td className="p-2 text-gray-700">{address.state}</td>
                          <td className="p-2 text-gray-700">{address.zip}</td>
                          <td className="p-2 text-gray-700">{address.current}</td>
                          <td className="p-2 text-gray-700">{address.billTo}</td>
                          <td className="p-2">
                            <div className="flex gap-1">
                              <button 
                                onClick={() => handleEditAddress(index)}
                                className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white rounded p-1.5"
                                title="Edit address"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                              {hasPermission('can_delete') && (
                              <button 
                                onClick={() => handleDeleteAddress(index)}
                                className="bg-[#DC3545] hover:bg-[#C82333] text-white rounded p-1.5"
                                title="Delete address"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Customer Projects */}
            <div className="bg-white rounded shadow p-6">
              <h2 className="text-xl text-[#5B8DB8] font-light mb-4">Customer Projects</h2>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2">
                      <th className="text-left p-2 text-gray-600 font-normal">ID</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Street Address</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Location</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Type</th>
                      <th className="text-left p-2 text-gray-600 font-normal">PND Date</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Start Date</th>
                      <th className="text-left p-2 text-gray-600 font-normal">Completion Date</th>
                      <th className="text-left p-2 text-gray-600 font-normal"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="p-4 text-center text-gray-500 italic">
                          No projects found
                        </td>
                      </tr>
                    ) : (
                      projects.map((project, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="p-2 text-gray-700">{project.id}</td>
                          <td className="p-2">
                            <MapsLink address={project.streetAddress} testid={`project-address-link-${project.id}`} />
                          </td>
                          <td className="p-2 text-gray-700">{project.location}</td>
                          <td className="p-2">
                            <span className="bg-[#00BFFF] text-white px-3 py-1 rounded text-xs">
                              {project.type}
                            </span>
                          </td>
                          <td className="p-2 text-gray-700">{project.pndDate}</td>
                          <td className="p-2 text-gray-700">{project.startDate}</td>
                          <td className="p-2 text-gray-700">{project.completionDate}</td>
                          <td className="p-2">
                            <div className="flex gap-1">
                              {hasPermission('can_update') && (
                              <Link
                                to={`/projects/${project.id}`}
                                className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white rounded p-1.5 inline-flex items-center justify-center"
                                data-testid={`edit-project-${project.id}`}
                                title="Edit project (right-click to open in new tab)"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </Link>
                              )}
                              {user?.is_admin && (
                              <button
                                className="bg-[#DC3545] hover:bg-[#C82333] text-white rounded p-1.5"
                                onClick={() => handleDeleteProject(project.id)}
                                data-testid={`delete-project-${project.id}`}
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-6 flex gap-4">
          <button
            onClick={handleBack}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded transition-colors"
          >
            Back
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-6 py-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Customer'}
          </button>
        </div>
      </div>
      
      {/* Delete Address Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteAddressDialog.isOpen}
        title="Delete Address"
        message="Are you sure you want to delete this address?"
        onConfirm={confirmDeleteAddress}
        onCancel={cancelDeleteAddress}
      />
    </Layout>
  );
};

export default CustomerDetail;
